/**
 * Prioridad del ticket: etiquetas, orden y tono, en un único sitio.
 *
 * Mismo criterio que config/roles.ts — las etiquetas en español se definen aquí
 * y no se duplican por pantalla, que es como acabaron divergiendo las de rol.
 *
 * **Prioridad no es SLA.** No hay plazo ni vencimiento detrás de estos niveles;
 * son urgencia percibida y sirven para ordenar la bandeja. Los plazos vendrán
 * cuando haya volumen real con el que calibrarlos.
 *
 * Se pintan con la variante `outline` del Badge (chip con contorno), nunca
 * `solid`: el relleno es del estado. Que sean FORMAS distintas es lo que evita
 * confundir un «En progreso» ámbar con un «Alta» ámbar en la misma cabecera.
 */

import type { BadgeTone } from '../components/Badge';

export type Priority = 'baja' | 'media' | 'alta' | 'critica';

/** De menos a más urgente. Es el orden de los selectores. */
export const PRIORITY_OPTIONS: Priority[] = ['baja', 'media', 'alta', 'critica'];

export const PRIORITY_LABELS: Record<Priority, string> = {
  baja: 'Baja',
  media: 'Media',
  alta: 'Alta',
  critica: 'Crítica',
};

/**
 * Los dos niveles tranquilos comparten el gris: no compiten por la atención.
 * Lo que hace legible la lista es que solo alta y crítica tengan color.
 */
export const PRIORITY_TONE: Record<Priority, BadgeTone> = {
  baja: 'slate',
  media: 'slate',
  alta: 'amber',
  critica: 'red',
};

export const PRIORITY_HINTS: Record<Priority, string> = {
  baja: 'Puede esperar. Nadie está bloqueado.',
  media: 'Curso normal. Es el valor por defecto.',
  alta: 'Una persona no puede trabajar.',
  critica: 'Un servicio caído o varias personas paradas.',
};

export const DEFAULT_PRIORITY: Priority = 'media';

export function priorityLabel(p: string | null | undefined): string {
  if (!p) return '';
  return PRIORITY_LABELS[p as Priority] ?? p;
}

export function priorityTone(p: string | null | undefined): BadgeTone {
  return PRIORITY_TONE[p as Priority] ?? 'slate';
}

/**
 * Si vale la pena gastar una línea de la fila en enseñarla.
 *
 * `media` es el default y sería la mayoría de las filas: pintarla en todas
 * añade un chip que no dice nada y le roba peso al que sí (crítica). En el
 * DETALLE se muestra siempre — allí no compite con nada y su ausencia se leería
 * como "no tiene prioridad".
 */
export function isNotablePriority(p: string | null | undefined): boolean {
  return p === 'alta' || p === 'critica';
}
